import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { ArrowRightIcon, ExternalLinkIcon, ImageIcon, MapPinIcon, MousePointerClickIcon, Trash2Icon } from 'lucide-react'
import type { ApprovalPageType, ApprovalView, ApproveRequest, ChangeNote, ChangeRoundView, ChangesRequest, OrderView } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { formatDateTime } from '@/components/app/format'
import { OrderError, TabGate, useOrder } from '@/components/app/order'
import { PickFrame, elementLabel, previewPath, type Picked } from '@/components/app/pick-frame'
import { CompareScreens, ScreenFrame, type Pin } from '@/components/app/screens'
import { SearchableList } from '@/components/app/searchable-list'
import { FormError, LoadingState, errorMessage } from '@/components/app/states'
import { ViewportToggle, type Viewport } from '@/components/app/viewport-toggle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/o/$orderId/approve')({
  loader: ({ params, abortController }) =>
    api<ApprovalView>(`/orders/${encodeURIComponent(params.orderId)}/approval`, { signal: abortController.signal }),
  pendingComponent: () => <LoadingState label="Loading your new site" />,
  errorComponent: ({ error }) => (
    <TabGate tab="/o/$orderId/approve">
      <OrderError error={error} title="We could not load your site for approval" />
    </TabGate>
  ),
  component: () => (
    <TabGate tab="/o/$orderId/approve">
      <Approve />
    </TabGate>
  ),
})

/** A note being written; `id` is local only and dropped on submit. */
type Draft = ChangeNote

function useDrafts(orderId: string) {
  const key = `hh:drafts:${orderId}`
  const [drafts, setDrafts] = useState<Draft[]>(() => {
    try {
      const raw = sessionStorage.getItem(key)
      return raw ? (JSON.parse(raw) as Draft[]) : []
    } catch {
      return []
    }
  })
  useEffect(() => {
    try {
      if (drafts.length) sessionStorage.setItem(key, JSON.stringify(drafts))
      else sessionStorage.removeItem(key)
    } catch {
      // Storage unavailable (private mode): drafts live in memory only.
    }
  }, [key, drafts])
  return [drafts, setDrafts] as const
}

function Approve() {
  const view = Route.useLoaderData()
  const order = useOrder()
  const [drafts, setDrafts] = useDrafts(order.id)
  // While a sent round is being applied, the badge names that round, not the next one.
  const inProgress = order.state === 'changes_requested'
  const round = inProgress ? view.changeRound : Math.min(view.changeRound + 1, view.includedRounds)
  const roundsLeft = inProgress ? 1 : Math.max(0, view.includedRounds - view.changeRound)

  const pageLabel = (url: string | null) => {
    if (!url) return 'Whole site'
    const path = view.previewUrl ? previewPath(view.previewUrl, url) : null
    const p = view.pages.find((x) => x.oldUrl === url || (path !== null && x.newPath === path))
    const t = view.pageTypes.find((x) => x.sampleUrl === url)
    return p?.title ?? t?.name ?? url
  }

  return (
    <div className="flex flex-col gap-10">
      <section aria-labelledby="check" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="check" className="text-xl font-semibold">
            Check your new site
          </h2>
          {view.includedRounds > 0 && (
            <Badge variant="outline" className="h-6 px-2.5 text-sm">
              {roundsLeft > 0 ? `Round ${round} of ${view.includedRounds}` : `${view.includedRounds} of ${view.includedRounds} rounds used`}
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground">
          {view.canRequestChanges
            ? `Compare each kind of page with your current site. If something looks wrong, click the spot on the rebuilt page, point at it on the live page, or write a note. Send all your notes at once: one submission is one round of changes, and ${view.includedRounds} are included. When you are happy, approve the site.`
            : 'Compare each kind of page with your current site.'}
        </p>
        {view.siteUrl && (
          <a href={view.siteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1.5 underline underline-offset-4">
            Open the full rebuilt site in a new tab
            <ExternalLinkIcon className="size-4" aria-hidden />
          </a>
        )}
      </section>

      <PageTypes view={view} drafts={drafts} setDrafts={setDrafts} />

      <section aria-labelledby="notes" className="flex flex-col gap-4">
        <h2 id="notes" className="text-xl font-semibold">
          Your notes
        </h2>
        {view.canRequestChanges ? (
          <NotesForm view={view} drafts={drafts} setDrafts={setDrafts} pageLabel={pageLabel} orderId={order.id} />
        ) : (
          <p className="text-muted-foreground">
            {order.state === 'changes_requested'
              ? 'We are applying your notes. We email you when the updated site is ready to check, usually within a day.'
              : `You have used the ${view.includedRounds} included rounds of changes. If something is still wrong, reply to our latest email.`}
          </p>
        )}
      </section>

      {view.rounds.length > 0 && <Rounds rounds={view.rounds} pageLabel={pageLabel} />}

      <section aria-labelledby="pages" className="flex flex-col gap-3">
        <h2 id="pages" className="text-xl font-semibold">
          Every page and redirect
        </h2>
        <p className="text-sm text-muted-foreground">
          Each old address sends visitors and search engines to its new page automatically (a permanent redirect).
        </p>
        <SearchableList
          title="Pages"
          items={view.pages}
          text={(p) => `${p.title ?? ''} ${p.oldUrl} ${p.newPath}`}
          render={(p) => (
            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
              <a href={p.oldUrl} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate text-muted-foreground underline-offset-4 hover:underline">
                {p.title ?? p.oldUrl}
              </a>
              <ArrowRightIcon className="hidden size-4 shrink-0 text-muted-foreground sm:block" aria-label="becomes" />
              <a href={p.newUrl} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate underline underline-offset-4">
                {p.newPath}
              </a>
            </div>
          )}
          empty="No pages listed yet."
        />
        <SearchableList
          title="Redirects"
          items={view.redirects}
          text={(r) => `${r.from} ${r.to}`}
          render={(r) => (
            <div className="flex flex-col gap-0.5 font-mono text-xs sm:flex-row sm:items-center sm:gap-2">
              <span className="min-w-0 break-all text-muted-foreground">{r.from}</span>
              <ArrowRightIcon className="hidden size-3 shrink-0 text-muted-foreground sm:block" aria-label="redirects to" />
              <span className="min-w-0 break-all">{r.to}</span>
            </div>
          )}
          empty="No redirects listed yet."
        />
      </section>

      {view.canApprove && <ApproveBox order={order} hasDrafts={drafts.length > 0} />}
    </div>
  )
}

function PageTypes({
  view,
  drafts,
  setDrafts,
}: {
  view: ApprovalView
  drafts: Draft[]
  setDrafts: (fn: (d: Draft[]) => Draft[]) => void
}) {
  const [viewport, setViewport] = useState<Viewport>('desktop')
  const [selected, setSelected] = useState(view.pageTypes[0]?.key ?? '')
  const [pending, setPending] = useState<{ x: number; y: number; type: ApprovalPageType } | null>(null)
  const [live, setLive] = useState(false)
  const type = view.pageTypes.find((t) => t.key === selected) ?? view.pageTypes[0]
  const canPoint = view.canRequestChanges && !!view.previewUrl

  if (!type) return <p className="text-muted-foreground">Page screenshots are not ready yet.</p>

  const pins: Pin[] = drafts
    .map((d, i) => ({ d, label: String(i + 1) }))
    .filter(({ d }) => d.pageUrl === type.sampleUrl && d.viewport === viewport && d.x !== null && d.y !== null)
    .map(({ d, label }) => ({ x: d.x ?? 0, y: d.y ?? 0, label }))

  return (
    <section aria-labelledby="types" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="types" className="text-xl font-semibold">
          Side by side
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {canPoint && (
            <Button variant="outline" aria-pressed={live} onClick={() => setLive((l) => !l)}>
              {live ? <ImageIcon aria-hidden /> : <MousePointerClickIcon aria-hidden />}
              {live ? 'Back to screenshots' : 'Point at the live page'}
            </Button>
          )}
          <ViewportToggle value={viewport} onChange={setViewport} />
        </div>
      </div>
      {view.pageTypes.length > 1 && (
        <div role="group" aria-label="Kind of page" className="flex flex-wrap gap-2">
          {view.pageTypes.map((t) => (
            <button
              key={t.key}
              type="button"
              aria-pressed={t.key === type.key}
              onClick={() => setSelected(t.key)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                t.key === type.key ? 'border-foreground bg-foreground text-background' : 'border-border hover:bg-muted',
              )}
            >
              {t.name} <span className="opacity-70">({t.pageCount})</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-sm text-muted-foreground">
        Example page:{' '}
        <a href={type.sampleUrl} target="_blank" rel="noopener noreferrer" className="break-all underline underline-offset-4">
          {type.sampleUrl}
        </a>
        . Every {type.pageCount === 1 ? 'page' : `one of the ${type.pageCount} pages`} of this kind uses the same layout.
        {view.canRequestChanges && (live ? ' Click the part of the page your note is about.' : ' Click the rebuilt page to pin a note on a spot.')}
      </p>
      {live && view.previewUrl ? (
        <PointNote
          key={type.key}
          src={view.previewUrl.replace(/\/$/, '') + (view.pages.find((p) => p.oldUrl === type.sampleUrl)?.newPath ?? '/')}
          viewport={viewport}
          name={type.name}
          onAdd={(p, text) => setDrafts((d) => [...d, { id: crypto.randomUUID(), pageUrl: p.pageUrl, x: null, y: null, viewport, text, element: p.element }])}
        />
      ) : (
        <CompareScreens
          original={type.original}
          rebuilt={type.rebuilt}
          viewport={viewport}
          name={type.name}
          rebuiltSlot={
            <ScreenFrame
              src={type.rebuilt[viewport]}
              alt={`${type.name}, rebuilt site, ${viewport} view${view.canRequestChanges ? '. Click to pin a note.' : ''}`}
              viewport={viewport}
              pins={pins}
              onPin={view.canRequestChanges ? (x, y) => setPending({ x, y, type }) : undefined}
            />
          }
        />
      )}
      <PinDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onSave={(text) => {
          if (!pending) return
          const note: Draft = {
            id: crypto.randomUUID(),
            pageUrl: pending.type.sampleUrl,
            x: pending.x,
            y: pending.y,
            viewport,
            text,
          }
          setDrafts((d) => [...d, note])
          setPending(null)
        }}
        context={pending ? `${pending.type.name}, ${viewport} view` : ''}
      />
    </section>
  )
}

/** The rebuilt page, live: the customer picks an element and writes a note about it. */
function PointNote({ src, viewport, name, onAdd }: { src: string; viewport: Viewport; name: string; onAdd: (p: Picked, text: string) => void }) {
  const id = useId()
  const [picked, setPicked] = useState<Picked | null>(null)
  const [text, setText] = useState('')
  const textRef = useRef<HTMLTextAreaElement>(null)

  function add(e: FormEvent) {
    e.preventDefault()
    if (!picked || !text.trim()) return
    onAdd(picked, text.trim())
    setText('')
    setPicked(null)
  }

  return (
    <div className="flex flex-col gap-3">
      <PickFrame src={src} viewport={viewport} title={`${name}, rebuilt site, live. Point at the part your note is about.`} picked={picked} onPick={setPicked} onConfirm={() => textRef.current?.focus()} />
      <form onSubmit={add} className="flex flex-col gap-2 rounded-xl border border-border p-4">
        <Label htmlFor={`${id}-t`}>{picked ? `Note on ${elementLabel(picked.element)}` : 'Note on the selected part'}</Label>
        <Textarea
          ref={textRef}
          id={`${id}-t`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={2000}
          disabled={!picked}
          placeholder={picked ? 'Say what is different from your current site.' : 'Select a part of the page first.'}
        />
        <div>
          <Button type="submit" disabled={!picked || !text.trim()}>
            <MapPinIcon aria-hidden />
            Add note
          </Button>
        </div>
      </form>
    </div>
  )
}

function PinDialog({
  open,
  onClose,
  onSave,
  context,
}: {
  open: boolean
  onClose: () => void
  onSave: (text: string) => void
  context: string
}) {
  const id = useId()
  const [text, setText] = useState('')
  useEffect(() => {
    if (open) setText('')
  }, [open])
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (text.trim()) onSave(text.trim())
          }}
        >
          <DialogHeader>
            <DialogTitle>Note on this spot</DialogTitle>
            <DialogDescription>{context}. Say what is different from your current site.</DialogDescription>
          </DialogHeader>
          <Label htmlFor={`${id}-t`} className="sr-only">
            Note
          </Label>
          <Textarea id={`${id}-t`} value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000} />
          <DialogFooter>
            <DialogClose render={<Button variant="outline" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!text.trim()}>
              <MapPinIcon aria-hidden />
              Add note
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function NotesForm({
  view,
  drafts,
  setDrafts,
  pageLabel,
  orderId,
}: {
  view: ApprovalView
  drafts: Draft[]
  setDrafts: (fn: (d: Draft[]) => Draft[]) => void
  pageLabel: (url: string | null) => string
  orderId: string
}) {
  const id = useId()
  const router = useRouter()
  const [page, setPage] = useState<string>('')
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState(false)

  function add(e: FormEvent) {
    e.preventDefault()
    if (!text.trim()) return
    setDrafts((d) => [...d, { id: crypto.randomUUID(), pageUrl: page || null, x: null, y: null, viewport: null, text: text.trim() }])
    setText('')
  }

  async function submit() {
    setSubmitting(true)
    setError(null)
    try {
      const body: ChangesRequest = { notes: drafts.map(({ id: _id, ...rest }) => rest) }
      await api<{ round: number }>(`/orders/${encodeURIComponent(orderId)}/changes`, { method: 'POST', json: body })
      setDrafts(() => [])
      setConfirm(false)
      await router.invalidate()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {drafts.length > 0 && (
        <ol className="flex flex-col divide-y divide-border rounded-xl border border-border">
          {drafts.map((d, i) => (
            <li key={d.id} className="flex items-start gap-3 px-4 py-3">
              <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                {i + 1}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="text-sm text-muted-foreground">
                  {pageLabel(d.pageUrl)}
                  {d.x !== null && d.viewport && ` · pinned on ${d.viewport}`}
                  {d.element && ` · ${elementLabel(d.element)} on ${d.viewport ?? 'desktop'}`}
                </p>
                <p className="break-words whitespace-pre-wrap">{d.text}</p>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove note ${i + 1}`}
                onClick={() => setDrafts((all) => all.filter((x) => x.id !== d.id))}
              >
                <Trash2Icon />
              </Button>
            </li>
          ))}
        </ol>
      )}

      <form onSubmit={add} className="flex flex-col gap-3 rounded-xl border border-border p-4">
        <p className="font-medium">Write a note</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-page`}>Page</Label>
          <NativeSelect id={`${id}-page`} value={page} onChange={(e) => setPage(e.target.value)} className="w-full max-w-md">
            <NativeSelectOption value="">Whole site</NativeSelectOption>
            {view.pages.map((p) => (
              <NativeSelectOption key={p.oldUrl} value={p.oldUrl}>
                {p.title ? `${p.title} (${p.newPath})` : p.newPath}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-text`}>What should change?</Label>
          <Textarea id={`${id}-text`} value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000} />
        </div>
        <div>
          <Button type="submit" variant="outline" disabled={!text.trim()}>
            Add note
          </Button>
        </div>
      </form>

      <p className="text-sm text-muted-foreground">
        We fix differences from your current site. New pages or a new design are not part of the rebuild; if a note asks for
        that, we reply and explain.
      </p>
      <FormError message={error} />
      <div>
        <Button size="lg" disabled={drafts.length === 0 || submitting} onClick={() => setConfirm(true)}>
          Send {drafts.length || ''} {drafts.length === 1 ? 'note' : 'notes'}
        </Button>
      </div>

      <Dialog open={confirm} onOpenChange={(o) => !submitting && setConfirm(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send your notes?</DialogTitle>
            <DialogDescription>
              This uses round {Math.min(view.changeRound + 1, view.includedRounds)} of {view.includedRounds}. You cannot add
              notes to this round after sending.
            </DialogDescription>
          </DialogHeader>
          <FormError message={error} />
          <DialogFooter>
            <DialogClose render={<Button variant="outline" type="button" disabled={submitting} />}>Keep editing</DialogClose>
            <Button onClick={submit} disabled={submitting}>
              {submitting && <Spinner aria-hidden />}
              Send {drafts.length} {drafts.length === 1 ? 'note' : 'notes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const ROUND_STATUS: Record<ChangeRoundView['status'], string> = {
  open: 'In progress',
  applied: 'Applied',
  rejected: 'Not applied',
}

function Rounds({ rounds, pageLabel }: { rounds: ChangeRoundView[]; pageLabel: (url: string | null) => string }) {
  return (
    <section aria-labelledby="rounds" className="flex flex-col gap-3">
      <h2 id="rounds" className="text-xl font-semibold">
        Earlier rounds
      </h2>
      <ul className="flex flex-col gap-4">
        {[...rounds]
          .sort((a, b) => b.round - a.round)
          .map((r) => (
            <li key={r.round} className="rounded-xl border border-border p-4">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <p className="font-medium">Round {r.round}</p>
                <Badge variant={r.status === 'applied' ? 'secondary' : 'outline'}>{r.status === 'applied' && r.outOfScope.length ? 'Partly applied' : ROUND_STATUS[r.status]}</Badge>
                <span className="text-sm text-muted-foreground">{formatDateTime(r.createdAt)}</span>
              </div>
              <ol className="flex flex-col gap-3">
                {r.notes.map((n, i) => {
                  const reply = r.outOfScope.find((o) => o.noteId === n.id)
                  return (
                    <li key={n.id || i} className="flex flex-col gap-1">
                      <p className="text-sm text-muted-foreground">
                        {i + 1}. {pageLabel(n.pageUrl)}
                        {n.element && ` · ${elementLabel(n.element)}`}
                      </p>
                      <p className="break-words whitespace-pre-wrap">{n.text}</p>
                      {reply && (
                        <p className="rounded-lg bg-muted px-3 py-2 text-sm">
                          <span className="font-medium">Not applied: </span>
                          {reply.reason}
                        </p>
                      )}
                    </li>
                  )
                })}
              </ol>
            </li>
          ))}
      </ul>
    </section>
  )
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function ApproveBox({ order, hasDrafts }: { order: OrderView; hasDrafts: boolean }) {
  const id = useId()
  const router = useRouter()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState(order.contactEmail ?? order.email ?? '')
  const [touched, setTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invalid = !EMAIL_RE.test(email.trim())

  async function approve(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (invalid) return
    setBusy(true)
    setError(null)
    try {
      const body: ApproveRequest = { contactEmail: email.trim() }
      await api<OrderView>(`/orders/${encodeURIComponent(order.id)}/approve`, { method: 'POST', json: body })
      setOpen(false)
      try {
        sessionStorage.removeItem(`hh:drafts:${order.id}`)
      } catch {
        // Storage unavailable: nothing to clear.
      }
      await router.invalidate()
      await navigate({ to: '/o/$orderId/domain', params: { orderId: order.id } })
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-labelledby="approve" className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-6">
      <h2 id="approve" className="text-xl font-semibold">
        Happy with it?
      </h2>
      <p className="text-muted-foreground">
        Approve the site and we prepare it for your domain. Next, you make one change in your domain settings and the new
        site goes live.
      </p>
      {hasDrafts && (
        <p className="text-sm text-warning">You have notes you have not sent. Approving discards them.</p>
      )}
      <Button size="lg" onClick={() => setOpen(true)}>
        Approve my site
      </Button>

      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent>
          <form onSubmit={approve} noValidate className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>Approve your site</DialogTitle>
              <DialogDescription>Confirm where messages from your contact form should go.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-email`}>Contact form email</Label>
              <Input
                id={`${id}-email`}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={touched && invalid}
                aria-describedby={`${id}-hint`}
                className="h-10"
              />
              <p id={`${id}-hint`} className={cn('text-sm', touched && invalid ? 'text-destructive' : 'text-muted-foreground')}>
                {touched && invalid ? 'Enter an email address like name@example.com.' : 'You can change it later by writing to us.'}
              </p>
            </div>
            <FormError message={error} />
            <DialogFooter>
              <DialogClose render={<Button variant="outline" type="button" disabled={busy} />}>Cancel</DialogClose>
              <Button type="submit" disabled={busy}>
                {busy && <Spinner aria-hidden />}
                Approve
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
